import SuspenseLoading from '@components/commons/SuspenseLoading';
import {
  AuthLayout,
  HomeLayout,
  FacilityLayout,
  PreRegistrationLayout,
} from '@components/layouts';
import DriverRegisterLayout from '@components/layouts/DriverRegisterLayout/DriverRegisterLayout';
import {
  Login,
  Dashboard,
  PreRegistrationDetail,
  DriverManagement,
  ReceptionManagement,
  DailyReport,
  DriverForm,
  DriverRegisterHome,
  ForceChangePassword,
  PreRegistrationList,
  DriverManagementDetail,
  Facility,
  RegisterAccount,
  Account,
  Policy,
  Camera,
  LowQualityFacePhoto,
  RegisterTemp,
  RegisterComplete,
  SystemConfiguration,
  SettingSMS,
  NotificationSMS,
  SecurityBox,
  ChangePassword,
  VerifyComplete,
  ForgotPassword,
} from '@components/pages';
import NotFoundPage from '@components/pages/home/DriverRegister/NotFoundPage';
import DriverRegisterSuccess from '@components/pages/home/DriverRegister/DriverRegisterSuccess';
import { useAppSelector } from '@hooks';
import { Routes } from '@utils/constants';
import { ElementType, FC, ReactNode, Suspense } from 'react';
import {
  Routes as BrowserRoutes,
  Navigate,
  Route,
  useLocation,
} from 'react-router-dom';
import { USER_ROLE } from '@utils/constants/enums';

const publicRoutes: IAppRoute[] = [
  {
    path: Routes.LOGIN,
    element: Login,
  },
  {
    path: Routes.FORGOT_PASSWORD,
    element: ForgotPassword,
  },
];

const facilityRoutes: IAppRoute[] = [
  { path: Routes.FACILITY, element: Facility },
];

const accountRoutes: IAppRoute[] = [
  { path: Routes.ACCOUNT, element: Account },
  {
    path: Routes.ACCOUNT_REGISTER,
    element: RegisterAccount,
    blockManager: true,
  },
  { path: Routes.ACCOUNT_DETAIL, element: RegisterAccount },
  { path: Routes.CHANGE_PASSWORD, element: ChangePassword },
];

const preRegistrationRoutes: IAppRoute[] = [
  {
    path: Routes.POLICY,
    element: Policy,
  },
  {
    path: Routes.PRE_REGISTER_CAMERA,
    element: Camera,
  },
  {
    path: Routes.LOW_QUALITY_FACE_PHOTO,
    element: LowQualityFacePhoto,
  },
  {
    path: Routes.REGISTER_TEMP,
    element: RegisterTemp,
  },
  {
    path: Routes.REGISTER_COMPLETE,
    element: RegisterComplete,
  },
];

const phoneVerificationRoutes: IAppRoute[] = [
  {
    path: Routes.VERIFY_PHONE_DRIVER,
    element: VerifyComplete,
  },
  {
    path: Routes.VERIFY_PHONE_SHORT_TMP,
    element: VerifyComplete,
  },
  {
    path: Routes.VERIFY_PHONE_SHORT_DRIVER,
    element: VerifyComplete,
  },
];

const privateRoutes: IAppRoute[] = [
  { path: Routes.DASHBOARD, element: Dashboard },
  { path: Routes.PRE_REGISTRATION_LIST, element: PreRegistrationList },
  { path: Routes.PRE_REGISTRATION_DETAIL, element: PreRegistrationDetail },
  { path: Routes.DRIVER_MANAGEMENT, element: DriverManagement },
  { path: Routes.DRIVER_MANAGEMENT_DETAIL, element: DriverManagementDetail },
  { path: Routes.RECEPTION_HISTORY, element: ReceptionManagement },
  { path: Routes.DAILY_REPORT, element: DailyReport },
  {
    path: Routes.SYSTEM_CONFIGURATION,
    element: SystemConfiguration,
    blockReceptionist: true,
  },
  { path: Routes.SETTING_SMS, element: SettingSMS },
  { path: Routes.NOTIFICATION_LIST, element: NotificationSMS },
  {
    path: Routes.DRIVER_REGISTER_HOME,
    element: DriverRegisterHome,
  },
  {
    path: Routes.SECURITY_BOX,
    element: SecurityBox,
  },
  {
    path: Routes.CHANGE_PASSWORD,
    element: ChangePassword,
  },
];

const withSuspense = (Component: ElementType) => (
  <Suspense fallback={<SuspenseLoading />}>
    <Component />
  </Suspense>
);

/* ===================== GUARDS ===================== */

const RequireForceChangePassword: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAppSelector(state => state.auth);

  if (!user) {
    return <Navigate to={Routes.LOGIN} replace />;
  }

  if (!user.isChangePass) {
    return <Navigate to={Routes.DASHBOARD} replace />;
  }
  return <>{children}</>;
};

const GuestOnly: FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAppSelector(state => state.auth);
  const { tenant } = useAppSelector(state => state.facility);

  if (user) {
    return (
      <Navigate to={tenant ? Routes.DASHBOARD : Routes.FACILITY} replace />
    );
  }
  return <>{children}</>;
};

const RequireAuth: FC<RequireAuthProps> = ({ children, blockReceptionist }) => {
  const { user } = useAppSelector(state => state.auth);
  const { tenant } = useAppSelector(state => state.facility);

  if (!user) {
    return <Navigate to={Routes.LOGIN} replace />;
  }

  if (user.isChangePass) {
    return <Navigate to={Routes.FORCE_CHANGE_PASSWORD} replace />;
  }

  if (blockReceptionist && user.role === USER_ROLE.RECEPTIONIST) {
    return (
      <Navigate to={tenant ? Routes.DASHBOARD : Routes.FACILITY} replace />
    );
  }
  return <>{children}</>;
};

const RequireFacility: FC<{ children: ReactNode }> = ({ children }) => {
  const { tenant } = useAppSelector(state => state.facility);
  const location = useLocation();
  const isChangePass = location.pathname === Routes.CHANGE_PASSWORD;

  if (!tenant) {
    if (isChangePass) {
      return <>{children}</>;
    }
    return <Navigate to={Routes.FACILITY} replace />;
  }
  return <>{children}</>;
};

const AppRoutes: FC = () => {
  const { user } = useAppSelector(state => state.auth);
  const { tenant } = useAppSelector(state => state.facility);

  return (
    <BrowserRoutes>
      {/* ROOT */}
      <Route
        path='/'
        element={
          <Navigate
            to={
              user
                ? tenant
                  ? Routes.DASHBOARD
                  : Routes.FACILITY
                : Routes.LOGIN
            }
            replace
          />
        }
      />

      {/* PUBLIC */}
      <Route
        element={
          <GuestOnly>
            <AuthLayout />
          </GuestOnly>
        }
      >
        {publicRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={withSuspense(route.element)}
          />
        ))}
      </Route>

      {/* FORCE CHANGE PASSWORD */}
      <Route
        element={
          <RequireForceChangePassword>
            <AuthLayout />
          </RequireForceChangePassword>
        }
      >
        <Route
          path={Routes.FORCE_CHANGE_PASSWORD}
          element={withSuspense(ForceChangePassword)}
        />
      </Route>

      {/* PRE REGISTRATION */}
      <Route
        element={
          <GuestOnly>
            <PreRegistrationLayout />
          </GuestOnly>
        }
      >
        {preRegistrationRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={withSuspense(route.element)}
          />
        ))}
      </Route>

      {/* Phone verification must also be accessible to signed-in users. */}
      <Route element={<PreRegistrationLayout />}>
        {phoneVerificationRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={withSuspense(route.element)}
          />
        ))}
      </Route>

      {/* FACILITY */}
      <Route
        element={
          <RequireAuth>
            <FacilityLayout />
          </RequireAuth>
        }
      >
        {facilityRoutes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            element={withSuspense(route.element)}
          />
        ))}
      </Route>

      {/* ACCOUNT */}
      <Route
        element={
          <RequireAuth blockReceptionist>
            <FacilityLayout />
          </RequireAuth>
        }
      >
        {accountRoutes.map(route => {
          const isBlockManager =
            route.blockManager && user?.role === USER_ROLE.MANAGER;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                isBlockManager ? (
                  <Navigate to={Routes.FACILITY} replace />
                ) : (
                  withSuspense(route.element)
                )
              }
            />
          );
        })}
      </Route>

      {/* PRIVATE */}
      <Route
        element={
          <RequireAuth>
            <RequireFacility>
              <HomeLayout />
            </RequireFacility>
          </RequireAuth>
        }
      >
        {privateRoutes.map(route => {
          const isBlockReceptionist =
            route.blockReceptionist && user?.role === USER_ROLE.RECEPTIONIST;

          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                isBlockReceptionist ? (
                  <Navigate to={Routes.DASHBOARD} replace />
                ) : (
                  withSuspense(route.element)
                )
              }
            />
          );
        })}
        <Route index element={<Navigate to={Routes.DASHBOARD} replace />} />
      </Route>

      {/* DRIVER REGISTER */}
      <Route path={Routes.DRIVER_REGISTER} element={<DriverRegisterLayout />}>
        <Route index element={withSuspense(DriverForm)} />
        <Route path='success' element={withSuspense(DriverRegisterSuccess)} />
      </Route>

      {/* 404 */}
      <Route path='/404' element={withSuspense(NotFoundPage)} />

      <Route
        path='*'
        element={
          <Navigate
            to={
              user
                ? tenant
                  ? Routes.DASHBOARD
                  : Routes.FACILITY
                : Routes.LOGIN
            }
            replace
          />
        }
      />
    </BrowserRoutes>
  );
};

export default AppRoutes;
